/* eslint-disable-next-line import/prefer-default-export */
import { batch } from 'react-redux';
import {
  hasPhoneConstraint,
  hasPostcodeConstraint,
} from '../../Components/__helpers__/component.helpers';
import {
  findComponentByFieldName,
  getOptionValueFromId,
} from '../../Helpers/components';
import { exposeEventToPartner } from '../../Helpers/events';
import { validatePhoneSaga } from '../../Lib/redux/sagas/ValidatePhone/validatePhone';
import { validatePostcodeSaga } from '../../Lib/redux/sagas/ValidatePostcode/validatePostcode';
import {
  clearSelectedOptionsAndValue,
  selectOptionAndUpdateValue,
  setComponentValidationStatus,
  setSelectedOptions,
  updateDefaultValue,
  updateMaskedValue,
  updateValue,
} from '../../Lib/redux/slices/components';
import {
  setAdvancePageStatus,
  setQuestionAndAnswers,
} from '../../Lib/redux/slices/sessionDataPopulation';
import { mask } from '../../Utils/masking';
import { handleAnswerQuestionSideEffectsSaga } from '../actionCreators';

// eslint-disable-next-line import/prefer-default-export
export const parseSessionDataAndPopulateFormFields = ({
  sessionData,
  components,
  dispatch,
  iFrameId,
  countryCode,
  formId,
}) => {
  let shouldAttemptAdvancingToUnansweredQuestion = false;

  batch(() => {
    Object.keys(sessionData).forEach((fieldName) => {
      const value = sessionData[fieldName];
      const componentOrError = findComponentByFieldName(
        fieldName,
        value,
        components
      );

      if (componentOrError) {
        if (componentOrError.errorObject) {
          exposeEventToPartner({
            eventType: 'formError',
            eventObject: {
              ...componentOrError.errorObject,
              iFrameId,
              formId,
            },
            errorObject: {
              message: `Failed to populate form field`,
            },
          });
        } else {
          const { componentId, optionId } = componentOrError;
          const optionIdString = String(optionId);
          const formFieldType = componentOrError.fieldType;
          const component = components[`component_${componentId}`];
          const constraints = component?.validation?.constraints;

          const includesPhoneConstraints = hasPhoneConstraint({
            component,
          });

          const includesPostcodeConstraints = hasPostcodeConstraint({
            component,
          });

          const optionValue = getOptionValueFromId(component, optionId);

          switch (formFieldType) {
            case 'hidden':
              dispatch(updateDefaultValue({ componentId, value }));
              break;
            case 'textfield':
              if (includesPhoneConstraints) {
                batch(() => {
                  dispatch(updateValue({ componentId, value }));
                  dispatch(
                    validatePhoneSaga({
                      country: countryCode,
                      number: value,
                      componentId,
                      iFrameId,
                      constraints,
                    })
                  );
                });
              } else if (includesPostcodeConstraints) {
                batch(() => {
                  dispatch(updateValue({ componentId, value }));
                  dispatch(
                    validatePostcodeSaga({
                      countryCode,
                      value,
                      componentId,
                      iFrameId,
                    })
                  );
                });
                const maskPattern = component?.input?.maskPattern;
                if (maskPattern) {
                  dispatch(
                    updateMaskedValue({
                      componentId,
                      maskedValue: mask({ maskPattern, value }),
                    })
                  );
                }
              } else {
                dispatch(updateValue({ componentId, value }));
                dispatch(
                  setComponentValidationStatus({
                    componentId,
                    countryCode,
                    iFrameId,
                    formId,
                  })
                );
              }
              dispatch(handleAnswerQuestionSideEffectsSaga({ componentId }));
              shouldAttemptAdvancingToUnansweredQuestion = true;
              break;
            case 'select':
              batch(() => {
                dispatch(
                  updateValue({
                    componentId,
                    value: optionValue,
                  })
                );
                dispatch(
                  setSelectedOptions({
                    componentId,
                    selectedOptions: [optionIdString],
                  })
                );
                dispatch(
                  setComponentValidationStatus({
                    componentId,
                    countryCode,
                    iFrameId,
                    formId,
                  })
                );
                dispatch(handleAnswerQuestionSideEffectsSaga({ componentId }));
              });
              shouldAttemptAdvancingToUnansweredQuestion = true;
              break;
            case 'radios':
              batch(() => {
                dispatch(
                  clearSelectedOptionsAndValue({
                    componentId,
                  })
                );
                dispatch(
                  selectOptionAndUpdateValue({
                    componentId,
                    optionId: optionIdString,
                  })
                );
                dispatch(
                  setComponentValidationStatus({
                    componentId,
                    countryCode,
                    iFrameId,
                    formId,
                  })
                );
                dispatch(handleAnswerQuestionSideEffectsSaga({ componentId }));
              });
              shouldAttemptAdvancingToUnansweredQuestion = true;
              break;
            case 'checkboxes':
              batch(() => {
                dispatch(
                  selectOptionAndUpdateValue({
                    componentId,
                    optionId: optionIdString,
                  })
                );
                dispatch(
                  setComponentValidationStatus({
                    componentId,
                    countryCode,
                    iFrameId,
                    formId,
                  })
                );
                dispatch(handleAnswerQuestionSideEffectsSaga({ componentId }));
              });
              shouldAttemptAdvancingToUnansweredQuestion = true;
              break;
            default:
              // eslint-disable-next-line no-console
              console.warn(`Unexpected form field type: ${formFieldType}`);
              break;
          }
        }
      }
    });
  });

  dispatch(setQuestionAndAnswers({ questionAndAnswers: sessionData }));
  if (shouldAttemptAdvancingToUnansweredQuestion === true) {
    setTimeout(() => {
      // On mobile devices, the transition happens twice if we don't use setTimeout here
      // setTimeout is ensuring that the current execution context is finished before trying to advance the page
      dispatch(setAdvancePageStatus({ advancePageStatus: 'start' }));
    }, 0);
  }
};

export const extractParentPageTrustedFormOutOfSessionData = (
  sessionData,
  trustedFormMappedAdmiralQuestionId,
  trustedFormQuestionText
) => {
  /*
   * If the incoming session data contains a key-value pair for the Trusted
   * Form question, extract it out of the session data object.
   *
   * Note: GOLD-710 will need to revert this by deleting this function and the
   * function call, as per the ACs of the stop gap solution GOLD-742 and of
   * GOLD-710 itself.
   */
  const hasTrustedFormCertificateBeenReceived =
    Object.prototype.hasOwnProperty.call(
      sessionData,
      trustedFormQuestionText
    ) ||
    Object.prototype.hasOwnProperty.call(
      sessionData,
      trustedFormMappedAdmiralQuestionId
    );
  if (hasTrustedFormCertificateBeenReceived) {
    // Deeply clones this object by value to then mutate its clone (prevents a linter param-reassign error)
    const filteredSessionData = {};
    // eslint-disable-next-line no-console
    console.log(
      'MVF Info - Discarding Trusted Form Cert ID received from the parent page'
    );
    Object.keys(sessionData).forEach((property) => {
      if (
        property !== trustedFormMappedAdmiralQuestionId &&
        property !== trustedFormQuestionText
      ) {
        filteredSessionData[property] = sessionData[property];
      }
    });
    return filteredSessionData;
  }
  return sessionData;
};
